import kalvium from '@kalvi-education/kalvium-sdk'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import {
    BROWSER_ENROLLMENT_STATES,
    HIBU_METADATA_KEYS,
} from '@/enums/browserEnrollment'
import { resolveSquadEnforcedMode } from './enrollmentState.js'

import { AuthContext } from '../../contexts/AuthContext'

const isBrowserEnrollmentExtensionMessage = data =>
    data &&
    typeof data === 'object' &&
    data.type === 'FROM_EXTENSION' &&
    (data.payload === undefined ||
        data.payload === null ||
        typeof data.payload === 'string') &&
    (data.isMismatched === undefined ||
        typeof data.isMismatched === 'boolean') &&
    (data.isSnoozed === undefined || typeof data.isSnoozed === 'boolean') &&
    (data.requestId === undefined ||
        typeof data.requestId === 'string' ||
        data.requestId === null)

const SNOOZED_STATUS_REFRESH_INTERVAL_MS = import.meta.env.DEV
    ? 3 * 1000
    : 60 * 1000


// Existing hook coordinates metadata, temporary enrollment, and extension status.
// Keep this suppression narrow until the hook is split in a dedicated refactor.
// eslint-disable-next-line sonarjs/cognitive-complexity
const useBrowserEnrollment = () => {
    const { kalviumKc } = useContext(AuthContext)

    const token = kalviumKc.getParsedToken()
    const isMentor = token && token.role ? token.role.includes('mentor') : false

    const [isPermanentEidAccepted, setIsPermanentEidAccepted] = useState(false)
    const [isMismatched, setIsMismatched] = useState(false)
    const [isSnoozed, setIsSnoozed] = useState(false)
    const [requestId, setRequestId] = useState(null)
    const [temporaryEid, setTemporaryEid] = useState(null)
    const [isTemporaryEidAccepted, setIsTemporaryEidAccepted] = useState(false)
    const [extensionStatus, setExtensionStatus] = useState('not_received')
    const [temporaryLookup, setTemporaryLookup] = useState('not_requested')
    const [isBannerEnabled, setIsBannerEnabled] = useState(false)

    // Using ref for diagnostics purpose to log the actual data since its in use effect so we are using its ref to get the latest value
    const isBannerEnabledRef = useRef(isBannerEnabled)
    useEffect(() => {
        isBannerEnabledRef.current = isBannerEnabled
    }, [isBannerEnabled])
    const [squadEnforcedMode, setSquadEnforcedMode] = useState(() =>
        !isMentor && !kalviumKc?.getParsedToken()?.groups?.[0]
            ? { status: 'unavailable', value: 'null' }
            : { status: 'unknown', value: 'null' }
    )

    const fetchEnrollmentStatus = useCallback(async () => {
        if (!temporaryEid) return
        try {
            const response =
                await kalvium.services.browserEnrollment.getEnrollment(
                    temporaryEid,
                    import.meta.env.VITE_REALM
                )

            setTemporaryLookup(response?.data?.state ?? 'missing_data')
            setIsTemporaryEidAccepted(
                response?.data?.state === BROWSER_ENROLLMENT_STATES.ACCEPTED
            )

            return response?.data?.state // Return state so it can be used elsewhere
        } catch (err) {
            setTemporaryLookup('request_failed')
            console.error('[browser-enrollment] Temporary lookup failed', {
                status: err?.response?.status ?? null,
            })
            setIsTemporaryEidAccepted(false)
            return null
        }
    }, [temporaryEid])

    const updateBannerStatus = useCallback(
        async squad => {
            // Skip banner status update for mentors
            if (isMentor || !squad) {
                setIsBannerEnabled(false)
                return
            }

            try {
                const bannerPayload = {
                    entityId: squad,
                    key: HIBU_METADATA_KEYS.ENROLLED_BROWSER_BANNER_ENABLED,
                    entityType: 'squad',
                }
                const data = await kalvium.services.metadata.get(bannerPayload)
                setIsBannerEnabled(data?.[0]?.value === 'true')
            } catch (error) {
                console.error('Unable to fetch metadata')
                setIsBannerEnabled(false)
            }
        },
        [isMentor]
    )

    useEffect(() => {
        let isCurrent = true
        const squadEnforcedModeSetup = async () => {
            const squad = kalviumKc?.getParsedToken()?.groups?.[0]

            // Skip banner and integrity checks for mentors
            if (isMentor) {
                if (isCurrent) {
                    setIsBannerEnabled(false)
                    setSquadEnforcedMode({
                        status: 'known',
                        value: 'disabled',
                    })
                }
                return
            }

            if (!squad) {
                if (isCurrent) {
                    setIsBannerEnabled(false)
                    setSquadEnforcedMode({
                        status: 'unavailable',
                        value: 'null',
                    })
                }
                return
            }

            await updateBannerStatus(squad)

            // set if browser checks should be enforced or not
            const payload = {
                entityId: squad,
                key: HIBU_METADATA_KEYS.ASSESSMENT_RB_CHECKS_ENFORCED_MODE,
                entityType: 'squad',
            }
            let data
            try {
                data = await kalvium.services.metadata.get(payload)
            } catch (error) {
                console.error('[browser-enrollment] Policy lookup failed', {
                    status: error?.response?.status ?? null,
                })
            }
            const isEnabled = data?.[0]?.value

            if (!isCurrent) return

            setSquadEnforcedMode(resolveSquadEnforcedMode(isEnabled))
        }

        squadEnforcedModeSetup()

        return () => {
            isCurrent = false
        }
    }, [isMentor, kalviumKc, updateBannerStatus])

    useEffect(() => {
        setTemporaryEid(localStorage.getItem('RB_ENROLLMENT_ID'))
    }, [])

    const requestEnrollmentStatus = useCallback(() => {
        window.postMessage(
            {
                type: 'COMMUNICATE_TO_HIBU',
                action: 'SEND_ENROLLMENT_STATUS',
                payload: 'Please send the enrollment status',
            },
            '*'
        )
    }, [])

    useEffect(() => {
        const temporaryEidSetup = async () => {
            if (temporaryEid) {
                let enrollmentData
                try {
                    enrollmentData =
                        await kalvium.services.browserEnrollment.getEnrollment(
                            temporaryEid,
                            import.meta.env.VITE_REALM
                        )
                } catch (err) {
                    setTemporaryLookup('request_failed')
                    console.error('[browser-enrollment] Temporary lookup failed', {
                        status: err?.response?.status ?? null,
                    })
                    // Don't remove enrollment ID on API failure - preserve it for retry
                    setIsTemporaryEidAccepted(false)
                    return
                }
                setTemporaryLookup(enrollmentData?.data?.state ?? 'missing_data')
                setIsTemporaryEidAccepted(
                    enrollmentData?.data?.state ===
                        BROWSER_ENROLLMENT_STATES.ACCEPTED
                )
                if (
                    !enrollmentData?.data ||
                    enrollmentData?.data?.state ===
                        BROWSER_ENROLLMENT_STATES.REJECTED ||
                    enrollmentData?.data?.state ===
                        BROWSER_ENROLLMENT_STATES.EXPIRED
                ) {
                    setIsTemporaryEidAccepted(false)
                    localStorage.removeItem('RB_ENROLLMENT_ID')
                    setTemporaryEid(null)
                }
            }
        }
        temporaryEidSetup()
    }, [temporaryEid])

    useEffect(() => {
        const handleMessage = event => {
            if (event.source !== window) return

            if (isBrowserEnrollmentExtensionMessage(event.data)) {
                const status = ['accepted', 'pending', 'rejected', 'expired'].includes(
                    event.data.payload
                ) ? event.data.payload : 'unexpected_status'
                setExtensionStatus(status)
                console.info('[browser-enrollment] Extension status received', {
                    status,
                    isMismatched: event.data.isMismatched,
                    isSnoozed: event.data.isSnoozed,
                    isBannerEnabled: isBannerEnabledRef.current,
                })
                if (event.data.payload === BROWSER_ENROLLMENT_STATES.ACCEPTED) {
                    setIsPermanentEidAccepted(true)
                }

                if (event.data.isMismatched !== undefined) {
                    setIsMismatched(event.data.isMismatched)
                }

                if (event.data.isSnoozed !== undefined) {
                    setIsSnoozed(event.data.isSnoozed)
                }

                if (event.data.requestId !== undefined) {
                    setRequestId(event.data.requestId)
                }
            }
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                requestEnrollmentStatus()
            }
        }

        window.addEventListener('message', handleMessage, false)
        window.addEventListener('focus', requestEnrollmentStatus)
        document.addEventListener('visibilitychange', handleVisibilityChange)

        requestEnrollmentStatus()
        const retryTimeoutId = setTimeout(requestEnrollmentStatus, 1500)

        return () => {
            clearTimeout(retryTimeoutId)
            window.removeEventListener('message', handleMessage, false)
            window.removeEventListener('focus', requestEnrollmentStatus)
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            )
        }
    }, [requestEnrollmentStatus])

    useEffect(() => {
        if (!isSnoozed) return undefined

        const interval = setInterval(
            requestEnrollmentStatus,
            SNOOZED_STATUS_REFRESH_INTERVAL_MS
        )
        return () => clearInterval(interval)
    }, [isSnoozed, requestEnrollmentStatus])

    return {
        extensionStatus,
        temporaryLookup,
        isPermanentEidAccepted,
        isMismatched,
        isSnoozed,
        requestId,
        isTemporaryEidAccepted,
        setIsTemporaryEidAccepted,
        setTemporaryEid,
        temporaryEid,
        squadEnforcedMode,
        fetchEnrollmentStatus,
        isBannerEnabled,
    }
}

export default useBrowserEnrollment
